/**
 * Passion-to-Career Mapping System
 * Vision 2.0 Phase 3: Help passion-seekers find careers aligned with their interests
 *
 * Structure:
 * PASSION: Music
 * ├── DIRECT: Performing Musician, Recording Artist
 * ├── ADJACENT: Audio Engineer, Music Producer, Sound Designer
 * ├── SUPPORTING: Music Licensing, Event Production, Music Therapy
 * └── GIG: Freelance mixing, Beat production, Online lessons
 */

/**
 * Passion Categories - Main interests/hobbies users might have
 */
export const PASSION_CATEGORIES = {
  // Creative Arts
  music: {
    id: 'music',
    label: 'Music & Audio',
    icon: '🎵',
    description: 'Playing, creating, or working with music and sound',
    examples: ['Playing instruments', 'Listening to music', 'Making beats', 'Singing'],
  },
  visual_arts: {
    id: 'visual_arts',
    label: 'Visual Arts & Design',
    icon: '🎨',
    description: 'Drawing, painting, photography, or visual creation',
    examples: ['Drawing', 'Photography', 'Graphic design', 'Painting'],
  },
  writing: {
    id: 'writing',
    label: 'Writing & Storytelling',
    icon: '✍️',
    description: 'Creating written content, stories, or communication',
    examples: ['Blogging', 'Fiction writing', 'Journalism', 'Copywriting'],
  },
  film_video: {
    id: 'film_video',
    label: 'Film & Video',
    icon: '🎬',
    description: 'Creating, editing, or working with video content',
    examples: ['YouTube videos', 'Short films', 'Editing', 'Streaming'],
  },

  // Technology & Digital
  gaming: {
    id: 'gaming',
    label: 'Gaming',
    icon: '🎮',
    description: 'Playing, streaming, or creating video games',
    examples: ['Playing games', 'Streaming', 'Game modding', 'Esports'],
  },
  technology: {
    id: 'technology',
    label: 'Technology & Computers',
    icon: '💻',
    description: 'Working with computers, coding, or tech gadgets',
    examples: ['Coding', 'Building PCs', 'Cybersecurity', 'AI'],
  },

  // Physical & Sports
  fitness: {
    id: 'fitness',
    label: 'Fitness & Wellness',
    icon: '💪',
    description: 'Exercise, health, and physical training',
    examples: ['Working out', 'Yoga', 'Running', 'Nutrition'],
  },
  sports: {
    id: 'sports',
    label: 'Sports & Athletics',
    icon: '⚽',
    description: 'Playing or following competitive sports',
    examples: ['Team sports', 'Individual athletics', 'Coaching', 'Sports analysis'],
  },
  outdoor: {
    id: 'outdoor',
    label: 'Outdoors & Nature',
    icon: '🏕️',
    description: 'Hiking, camping, or connecting with nature',
    examples: ['Hiking', 'Camping', 'Wildlife', 'Conservation'],
  },

  // People & Social
  helping: {
    id: 'helping',
    label: 'Helping People',
    icon: '🤝',
    description: 'Supporting, mentoring, or caring for others',
    examples: ['Volunteering', 'Mentoring', 'Caregiving', 'Counseling'],
  },
  teaching: {
    id: 'teaching',
    label: 'Teaching & Education',
    icon: '📚',
    description: 'Educating, training, or explaining concepts',
    examples: ['Tutoring', 'Training', 'Presenting', 'Course creation'],
  },
  social: {
    id: 'social',
    label: 'Social & Community',
    icon: '👥',
    description: 'Building communities and connecting people',
    examples: ['Event planning', 'Community building', 'Networking', 'Social media'],
  },

  // Building & Making
  building: {
    id: 'building',
    label: 'Building & Making',
    icon: '🔧',
    description: 'Creating physical things with your hands',
    examples: ['DIY projects', 'Woodworking', 'Crafts', 'Renovation'],
  },
  cooking: {
    id: 'cooking',
    label: 'Food & Cooking',
    icon: '🍳',
    description: 'Preparing food, baking, or culinary arts',
    examples: ['Cooking', 'Baking', 'Recipe creation', 'Food blogging'],
  },
  fashion: {
    id: 'fashion',
    label: 'Fashion & Style',
    icon: '👗',
    description: 'Clothing, styling, or personal appearance',
    examples: ['Styling', 'Design', 'Makeup', 'Personal shopping'],
  },

  // Animals & Nature
  animals: {
    id: 'animals',
    label: 'Animals & Pets',
    icon: '🐾',
    description: 'Working with or caring for animals',
    examples: ['Pet care', 'Training', 'Wildlife', 'Animal rescue'],
  },

  // Business & Finance
  business: {
    id: 'business',
    label: 'Business & Entrepreneurship',
    icon: '💼',
    description: 'Starting businesses, sales, or commerce',
    examples: ['Starting businesses', 'Sales', 'Marketing', 'Investing'],
  },

  // Science & Discovery
  science: {
    id: 'science',
    label: 'Science & Discovery',
    icon: '🔬',
    description: 'Research, experimentation, and understanding how things work',
    examples: ['Research', 'Experiments', 'Data analysis', 'Discovery'],
  },

  // Travel & Culture
  travel: {
    id: 'travel',
    label: 'Travel & Culture',
    icon: '✈️',
    description: 'Exploring new places and cultures',
    examples: ['Travel', 'Languages', 'Cultural exchange', 'Tourism'],
  },

  // Automotive & Mechanical
  automotive: {
    id: 'automotive',
    label: 'Cars & Mechanics',
    icon: '🚗',
    description: 'Working with vehicles and mechanical systems',
    examples: ['Car repair', 'Restoration', 'Racing', 'Motorcycles'],
  },
} as const;

export type PassionId = keyof typeof PASSION_CATEGORIES;

/**
 * Career alignment types - how a career relates to a passion
 */
export type CareerAlignmentType = 'direct' | 'adjacent' | 'supporting' | 'gig';

export interface PassionCareerMapping {
  passion_id: PassionId;
  alignment_type: CareerAlignmentType;
  description?: string;
}

/**
 * Passion tags that can be added to careers
 */
export interface PassionTag {
  passion_id: PassionId;
  alignment: CareerAlignmentType;
  relevance_score: number; // 1-100, how relevant this passion is
}

/**
 * Career passion mappings database
 * Maps passion categories to career IDs with alignment types
 */
export const PASSION_TO_CAREERS: Record<PassionId, {
  direct: string[];      // Core careers doing the passion
  adjacent: string[];    // Careers that work alongside the passion
  supporting: string[];  // Careers that support the passion industry
  gig: string[];         // Freelance/side hustle opportunities
}> = {
  music: {
    direct: [
      'sound_engineering_technician',
      'music_therapist',
      'audio_equipment_technician',
      'broadcast_technician',
    ],
    adjacent: [
      'event_planner',
      'social_media_manager',
      'video_editor',
      'recording_studio_technician',
    ],
    supporting: [
      'customer_service_representative',
      'retail_sales',
      'marketing_specialist',
      'administrative_assistant',
    ],
    gig: [
      'freelance_audio_mixing',
      'music_lessons',
      'dj_services',
      'podcast_editing',
    ],
  },

  visual_arts: {
    direct: [
      'graphic_designer',
      'multimedia_artist',
      'photographer',
      'web_designer',
      'ux_designer',
    ],
    adjacent: [
      'marketing_specialist',
      'social_media_manager',
      'video_editor',
      'print_production',
    ],
    supporting: [
      'art_gallery_assistant',
      'retail_sales',
      'customer_service_representative',
      'administrative_assistant',
    ],
    gig: [
      'freelance_design',
      'stock_photography',
      'logo_design',
      'portrait_photography',
    ],
  },

  writing: {
    direct: [
      'technical_writer',
      'content_writer',
      'copywriter',
      'editor',
      'grant_writer',
    ],
    adjacent: [
      'social_media_manager',
      'marketing_specialist',
      'public_relations_specialist',
      'communications_coordinator',
    ],
    supporting: [
      'administrative_assistant',
      'customer_service_representative',
      'legal_assistant',
      'research_assistant',
    ],
    gig: [
      'freelance_writing',
      'blogging',
      'ghostwriting',
      'resume_writing',
    ],
  },

  film_video: {
    direct: [
      'video_editor',
      'camera_operator',
      'broadcast_technician',
      'multimedia_artist',
    ],
    adjacent: [
      'social_media_manager',
      'marketing_specialist',
      'graphic_designer',
      'event_coordinator',
    ],
    supporting: [
      'production_assistant',
      'administrative_assistant',
      'customer_service_representative',
    ],
    gig: [
      'freelance_video_editing',
      'wedding_videography',
      'youtube_content',
      'corporate_videos',
    ],
  },

  gaming: {
    direct: [
      'game_tester',
      'technical_support_specialist',
      'computer_support_specialist',
      'software_developer',
    ],
    adjacent: [
      'social_media_manager',
      'community_manager',
      'video_editor',
      'graphic_designer',
    ],
    supporting: [
      'customer_service_representative',
      'retail_sales',
      'it_support',
    ],
    gig: [
      'game_streaming',
      'esports_coaching',
      'game_guides',
      'beta_testing',
    ],
  },

  technology: {
    direct: [
      'computer_support_specialist',
      'it_support_technician',
      'network_technician',
      'web_developer',
      'cybersecurity_analyst',
    ],
    adjacent: [
      'technical_writer',
      'project_coordinator',
      'data_analyst',
      'systems_administrator',
    ],
    supporting: [
      'customer_service_representative',
      'administrative_assistant',
      'sales_representative',
    ],
    gig: [
      'freelance_development',
      'it_consulting',
      'website_maintenance',
      'tech_support',
    ],
  },

  fitness: {
    direct: [
      'personal_trainer',
      'fitness_instructor',
      'physical_therapy_aide',
      'recreation_worker',
    ],
    adjacent: [
      'health_coach',
      'nutrition_counselor',
      'sports_coach',
      'wellness_coordinator',
    ],
    supporting: [
      'customer_service_representative',
      'retail_sales',
      'administrative_assistant',
      'marketing_specialist',
    ],
    gig: [
      'personal_training',
      'online_coaching',
      'fitness_content',
      'group_classes',
    ],
  },

  sports: {
    direct: [
      'coach',
      'athletic_trainer',
      'recreation_worker',
      'sports_official',
    ],
    adjacent: [
      'personal_trainer',
      'event_coordinator',
      'sports_marketing',
      'physical_therapy_aide',
    ],
    supporting: [
      'customer_service_representative',
      'retail_sales',
      'administrative_assistant',
    ],
    gig: [
      'youth_coaching',
      'sports_officiating',
      'private_lessons',
      'sports_photography',
    ],
  },

  outdoor: {
    direct: [
      'park_ranger',
      'forestry_technician',
      'landscaper',
      'environmental_technician',
      'wildlife_technician',
    ],
    adjacent: [
      'tour_guide',
      'recreation_worker',
      'conservation_technician',
      'agricultural_worker',
    ],
    supporting: [
      'retail_sales', // outdoor gear
      'customer_service_representative',
      'administrative_assistant',
    ],
    gig: [
      'outdoor_guide',
      'nature_photography',
      'camping_instruction',
      'trail_maintenance',
    ],
  },

  helping: {
    direct: [
      'social_work_assistant',
      'community_health_worker',
      'home_health_aide',
      'mental_health_technician',
      'substance_abuse_counselor',
    ],
    adjacent: [
      'case_manager',
      'patient_advocate',
      'crisis_counselor',
      'volunteer_coordinator',
    ],
    supporting: [
      'administrative_assistant',
      'customer_service_representative',
      'receptionist',
    ],
    gig: [
      'life_coaching',
      'peer_counseling',
      'support_groups',
      'volunteer_organizing',
    ],
  },

  teaching: {
    direct: [
      'teacher_assistant',
      'tutor',
      'training_specialist',
      'corporate_trainer',
      'childcare_worker',
    ],
    adjacent: [
      'curriculum_developer',
      'instructional_coordinator',
      'education_administrator',
      'librarian_technician',
    ],
    supporting: [
      'administrative_assistant',
      'customer_service_representative',
      'receptionist',
    ],
    gig: [
      'private_tutoring',
      'online_courses',
      'test_prep',
      'language_lessons',
    ],
  },

  social: {
    direct: [
      'event_planner',
      'public_relations_specialist',
      'community_manager',
      'social_media_manager',
    ],
    adjacent: [
      'marketing_specialist',
      'customer_success_specialist',
      'volunteer_coordinator',
      'hospitality_manager',
    ],
    supporting: [
      'customer_service_representative',
      'administrative_assistant',
      'receptionist',
    ],
    gig: [
      'event_coordination',
      'social_media_management',
      'community_building',
      'influencer_marketing',
    ],
  },

  building: {
    direct: [
      'carpenter',
      'electrician',
      'plumber',
      'hvac_technician',
      'construction_worker',
      'welder',
    ],
    adjacent: [
      'construction_manager',
      'building_inspector',
      'maintenance_technician',
      'estimator',
    ],
    supporting: [
      'retail_sales', // hardware/supplies
      'customer_service_representative',
      'administrative_assistant',
    ],
    gig: [
      'handyman_services',
      'furniture_making',
      'renovation_projects',
      'custom_builds',
    ],
  },

  cooking: {
    direct: [
      'chef',
      'cook',
      'baker',
      'food_prep_worker',
      'caterer',
    ],
    adjacent: [
      'food_service_manager',
      'restaurant_manager',
      'nutrition_counselor',
      'food_photographer',
    ],
    supporting: [
      'server',
      'customer_service_representative',
      'retail_sales',
    ],
    gig: [
      'personal_chef',
      'catering',
      'meal_prep',
      'food_blogging',
    ],
  },

  fashion: {
    direct: [
      'fashion_designer',
      'tailor',
      'personal_stylist',
      'makeup_artist',
      'hairstylist',
    ],
    adjacent: [
      'retail_buyer',
      'visual_merchandiser',
      'fashion_photographer',
      'marketing_specialist',
    ],
    supporting: [
      'retail_sales',
      'customer_service_representative',
      'administrative_assistant',
    ],
    gig: [
      'personal_styling',
      'alterations',
      'fashion_consulting',
      'wardrobe_planning',
    ],
  },

  animals: {
    direct: [
      'veterinary_technician',
      'animal_trainer',
      'groomer',
      'kennel_attendant',
      'zookeeper',
    ],
    adjacent: [
      'pet_store_manager',
      'animal_rescue_coordinator',
      'wildlife_technician',
      'veterinary_receptionist',
    ],
    supporting: [
      'retail_sales',
      'customer_service_representative',
      'administrative_assistant',
    ],
    gig: [
      'pet_sitting',
      'dog_walking',
      'pet_photography',
      'animal_training',
    ],
  },

  business: {
    direct: [
      'business_analyst',
      'sales_representative',
      'marketing_specialist',
      'account_manager',
      'real_estate_agent',
    ],
    adjacent: [
      'project_manager',
      'operations_coordinator',
      'financial_analyst',
      'customer_success_specialist',
    ],
    supporting: [
      'administrative_assistant',
      'customer_service_representative',
      'bookkeeper',
    ],
    gig: [
      'consulting',
      'freelance_sales',
      'e-commerce',
      'dropshipping',
    ],
  },

  science: {
    direct: [
      'laboratory_technician',
      'research_assistant',
      'quality_control_inspector',
      'environmental_technician',
    ],
    adjacent: [
      'data_analyst',
      'technical_writer',
      'medical_laboratory_technician',
      'pharmaceutical_technician',
    ],
    supporting: [
      'administrative_assistant',
      'customer_service_representative',
      'receptionist',
    ],
    gig: [
      'science_tutoring',
      'research_consulting',
      'science_communication',
      'data_analysis',
    ],
  },

  travel: {
    direct: [
      'travel_agent',
      'tour_guide',
      'flight_attendant',
      'hotel_manager',
      'cruise_staff',
    ],
    adjacent: [
      'event_planner',
      'hospitality_worker',
      'interpreter',
      'tourism_marketing',
    ],
    supporting: [
      'customer_service_representative',
      'administrative_assistant',
      'receptionist',
    ],
    gig: [
      'tour_guide',
      'travel_blogging',
      'translation',
      'travel_photography',
    ],
  },

  automotive: {
    direct: [
      'automotive_technician',
      'diesel_mechanic',
      'auto_body_repairer',
      'motorcycle_mechanic',
    ],
    adjacent: [
      'parts_specialist',
      'service_advisor',
      'automotive_sales',
      'fleet_manager',
    ],
    supporting: [
      'retail_sales',
      'customer_service_representative',
      'administrative_assistant',
    ],
    gig: [
      'mobile_mechanic',
      'detailing',
      'restoration',
      'car_flipping',
    ],
  },
};

/**
 * Get careers that match a user's passions
 */
export function getCareersForPassion(
  passionId: PassionId,
  alignmentFilter?: CareerAlignmentType[]
): { career_id: string; alignment: CareerAlignmentType }[] {
  const mapping = PASSION_TO_CAREERS[passionId];
  if (!mapping) return [];

  const results: { career_id: string; alignment: CareerAlignmentType }[] = [];
  const alignments = alignmentFilter || ['direct', 'adjacent', 'supporting', 'gig'];

  for (const alignment of alignments) {
    const careers = mapping[alignment as CareerAlignmentType];
    if (careers) {
      for (const careerId of careers) {
        results.push({ career_id: careerId, alignment: alignment as CareerAlignmentType });
      }
    }
  }

  return results;
}

/**
 * Get all passions that relate to a career
 */
export function getPassionsForCareer(careerId: string): PassionCareerMapping[] {
  const results: PassionCareerMapping[] = [];

  for (const [passionId, mapping] of Object.entries(PASSION_TO_CAREERS)) {
    for (const [alignment, careers] of Object.entries(mapping)) {
      if (careers.includes(careerId)) {
        results.push({
          passion_id: passionId as PassionId,
          alignment_type: alignment as CareerAlignmentType,
        });
      }
    }
  }

  return results;
}

/**
 * Calculate passion alignment score for a career
 * Returns 0-100 based on how well the career aligns with user's passions
 */
export function calculatePassionScore(
  careerId: string,
  userPassions: PassionId[]
): { score: number; alignments: PassionCareerMapping[] } {
  if (!userPassions.length) {
    return { score: 0, alignments: [] };
  }

  const careerPassions = getPassionsForCareer(careerId);
  const matchingAlignments: PassionCareerMapping[] = [];

  let totalScore = 0;
  for (const passion of userPassions) {
    const alignment = careerPassions.find(cp => cp.passion_id === passion);
    if (alignment) {
      matchingAlignments.push(alignment);
      // Score based on alignment type
      switch (alignment.alignment_type) {
        case 'direct':
          totalScore += 100;
          break;
        case 'adjacent':
          totalScore += 70;
          break;
        case 'supporting':
          totalScore += 40;
          break;
        case 'gig':
          totalScore += 50;
          break;
      }
    }
  }

  // Average score across all passions
  const score = Math.round(totalScore / userPassions.length);

  return {
    score: Math.min(100, score),
    alignments: matchingAlignments,
  };
}

/**
 * Get top passion-aligned careers for a user
 */
export function getTopPassionCareers(
  userPassions: PassionId[],
  limit = 10
): { career_id: string; score: number; alignments: PassionCareerMapping[] }[] {
  // Collect all unique career IDs from user's passions
  const careerScores = new Map<string, { score: number; alignments: PassionCareerMapping[] }>();

  for (const passion of userPassions) {
    const careers = getCareersForPassion(passion);
    for (const { career_id } of careers) {
      if (!careerScores.has(career_id)) {
        const scoreData = calculatePassionScore(career_id, userPassions);
        careerScores.set(career_id, scoreData);
      }
    }
  }

  // Sort by score and return top results
  return Array.from(careerScores.entries())
    .map(([career_id, data]) => ({ career_id, ...data }))
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);
}

/**
 * Get alignment type label for display
 */
export function getAlignmentLabel(alignment: CareerAlignmentType): string {
  switch (alignment) {
    case 'direct':
      return 'Core Career';
    case 'adjacent':
      return 'Related Field';
    case 'supporting':
      return 'Industry Support';
    case 'gig':
      return 'Freelance/Side Hustle';
    default:
      return 'Related';
  }
}

/**
 * Get passion category info
 */
export function getPassionInfo(passionId: PassionId) {
  return PASSION_CATEGORIES[passionId] || null;
}

/**
 * Get all passion categories as options for a question
 */
export function getPassionOptions(): { id: PassionId; label: string; icon: string; description: string }[] {
  return Object.values(PASSION_CATEGORIES).map(p => ({
    id: p.id as PassionId,
    label: p.label,
    icon: p.icon,
    description: p.description,
  }));
}
